<template>
  <div class="h-full p-8 dark:bg-yb-gray-alt-dark">
    <h3 class="flex flex-row justify-between w-full mb-3">
      <translate>About</translate>
      <div class="flex flex-row flex-nowrap space-x-4 float-right">
        <a
          v-tooltip="'Display a report of open source licenses for components used to build Yellowbrick Manager'"
          data-test-id="about-3rd-party-icon"
          class="yb-link text-sm flex flex-row flex-nowrap items-center mr-1.5"
          href="/3rdpartylicenses.txt"
          target="_blank"
        >
          <div class="w-4 h-4 mr-1 inline-block">
            <yb-icon icon="file_plain" />
          </div>
          <span class="text-yb-brand-primary-dark dark:text-yb-brand-primary ml-0.5">3rd Party Licenses</span>
        </a>
        <a v-yb-clipboard:function="generateClipboardText" data-test-id="about-clipboard-icon" class="yb-link text-sm flex flex-row flex-nowrap items-center" @click="nothing">
          <div class="w-4 h-4 mr-1 inline-block">
            <yb-icon icon="clipboard" />
          </div>
          <span class="text-yb-brand-primary-dark dark:text-yb-brand-primary ml-0.5">Copy to Clipboard</span>
        </a>
      </div>
    </h3>
    <div>
      <div v-if="$store.get('global/loading') && userInstances.length == 0" class="flex flex-col w-full h-1/2 justify-center items-center">
        <yb-loading />
      </div>
      <div v-if="Object.keys(softwareAboutData).length > 0" class="max-w-[600px] mt-6">
        <h4>Shared Services</h4>
        <div class="w-full h-full border yb-border-content dark:bg-yb-gray-alt-darker rounded-sm mt-6 mx-6">
          <h6 class="w-full yb-header yb-header-label border-b yb-border-content">
            <translate>Yellowbrick Manager</translate>
          </h6>
          <table class="table-auto w-full h-full ml-2">
            <tbody>
              <tr>
                <td class="w-3/12	pl-4 pt-4 p-2 font-semibold">
                  <translate>Software Version</translate>
                </td>
                <td class="w-7/12	p-2 pt-4 font-light" data-test-id="software_version">
                  {{ softwareAboutData.buildVersion }}
                </td>
              </tr>
              <tr>
                <td class="w-3/12	pl-4 p-2 font-semibold">
                  <translate>Build Date</translate>
                </td>
                <td class="w-7/12	p-2 font-light">
                  {{ formatDateTimeFull(softwareAboutData.buildDate) }}
                </td>
              </tr>
              <tr>
                <td class="w-3/12	pl-4 pb-4 p-2 font-semibold">
                  <translate>Logged in As</translate>
                </td>
                <td class="w-7/12	p-2 pb-4 font-light" data-test-id="user_logged">
                  {{ user }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div v-if="userInstances.length > 0" class="flex flex-col max-w-[600px] my-6 pb-12">
        <h4>Instances</h4>
        <div
          v-for="(instance, index) in userInstances"
          :key="index"
          class="w-full h-full border yb-border-content dark:bg-yb-gray-alt-darker rounded-sm mt-6 mx-6"
        >
          <h6 class="w-full yb-header yb-header-label border-b yb-border-content flex flex-row items-center space-x-2">
            <yb-icon icon="instance" class="inline-block h-5 w-5" />
            <div>{{ instance.name }}</div>
          </h6>
          <table class="table-auto w-full h-full ml-2">
            <tbody>
              <tr>
                <td class="w-3/12 pl-4 pt-4 p-2 font-semibold">
                  <translate>System Version</translate>
                </td>
                <td class="w-7/12	p-2 pt-4 font-light" data-test-id="instance_version">
                  {{ instance.softwareVersion }}
                </td>
              </tr>
              <tr>
                <td class="w-3/12	pl-4 p-2 font-semibold">
                  <translate>Build Date</translate>
                </td>
                <td class="w-7/12	p-2 font-light">
                  {{ formatDateTimeFull(instance.buildDate) }}
                </td>
              </tr>
              <tr>
                <td class="w-3/12	pl-4 pb-4 p-2 font-semibold">
                  <translate>Status</translate>
                </td>
                <td class="w-7/12	p-2 pb-4 font-light">
                  <div class="flex flex-row items-center space-x-1">
                    <yb-icon v-tooltip="'Instance State: ' + instance.activeStatus.description" :icon="getOpIcon(instance.activeStatus.description)" class="w-4 h-4" :class="getStatusColorClass(instance.activeStatus.description)" />
                    <div>{{ instance.activeStatus.description }}</div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getInstance } from "@/auth"
import { capitalize, formatDateTimeFull } from '@/filters'
import useInstancesLoader from '@/app/instance/InstancesLoader'

export default {
  name: "About",
  setup () {
    definePageMeta({ scrolled: true })
    return {
      ...useInstancesLoader()
    }
  },
  data () {
    return {
      softwareAboutData: {},
      user: ''
    }
  },
  async mounted() {
    this.getUser()
    await this.getManagerInfo()
  },
  methods: {
    formatDateTimeFull,
    getOpIcon,
    getStatusColorClass,
    generateClipboardText() {
      let text = 'Yellowbrick Manager Info:\n'
      if (Object.keys(this.softwareAboutData).length > 0) {
        text = this.appendCopyTextWithTab("System Version", this.softwareAboutData.buildVersion, text)
        text = this.appendCopyTextWithTab("Build Date", this.softwareAboutData.buildDate, text)
        text = this.appendCopyTextWithTab("Logged in As", this.user, text)
      }
      for (const index in this.userInstances) {
        text = this.appendCopyText("Name", this.userInstances[index].name, text)
        text = this.appendCopyTextWithTab("System Version", this.userInstances[index].softwareVersion, text)
        text = this.appendCopyTextWithTab("Build Date", this.userInstances[index].buildDate, text)
        text = this.appendCopyTextWithTab("Revision", this.userInstances[index].revision, text)
      }
      return text
    },
    getManagerInfo() {
      const buildVersion = import.meta.env.VITE_BUILD_VERSION
      const buildDate = import.meta.env.VITE_BUILD_DATE

      // Parse software version into components.
      const buildVersionRe = /(\d+\.\d+\.\d+)-(\d+)\.([a-f0-9]{8})/
      const buildVersionParts = buildVersionRe.exec(buildVersion)
      if (buildVersionParts) {
        const ybdVersion = buildVersionParts[1]
        this.softwareAboutData = {
          buildVersion,
          buildDate,
          softwareVersion: ybdVersion.replace(/\.[0-9]+$/, ''),
          buildNumber: buildVersionParts[2],
          sha: buildVersionParts[3]
        }
      }
    },
    appendCopyText(key, value, text) {
      text += key.toString() + ": " + value + "\n"
      return text
    },
    appendCopyTextWithTab(key, value, text) {
      text += "\t" + key.toString() + ": " + value + "\n"
      return text
    },
    getUser() {
      this.user = capitalize(getInstance().userName || this.$store.get('global/settings@user.name'))
    },
    nothing() {
    }
  }
}
</script>

<style scoped lang="postcss">
table td:first-child {
  @apply whitespace-nowrap;
}
</style>
